import type { HTMLAttributes, HTMLProps, ReactNode } from 'react';
import { useEffect } from 'react';

import { Typography } from '@remarkable/ark-web';
import { twJoin, twMerge } from 'tailwind-merge';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { BackLink } from './BackLink';
import { Navbar } from './NavBar';

const Container = ({
  children,
  className,
  backLink = false,
  ...rest
}: {
  children: ReactNode;
  backLink?: boolean;
  className?: string;
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={twJoin('mx-auto w-full', className)} {...rest}>
      {backLink && (
        <BackLink
          className="absolute top-80 left-16"
          componentLocation={ComponentLocations.APP_CONTAINER}
        />
      )}
      {children}
    </div>
  );
};

interface HeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  className?: string;
}
const Header = (props: HeaderProps) => (
  <div
    className={twMerge(
      'm-auto my-32 px-16 text-left lm:mt-48 lm:px-24 ll:mt-48 ll:mb-48 ll:px-32',
      props.className
    )}
  >
    <Typography variant="responsive-heading-lg" as="h1">
      {props.title}
    </Typography>
    {props.subtitle && (
      <p className="mt-16 mb-0 responsive-body-md ls:mt-20">{props.subtitle}</p>
    )}
    {props.children}
  </div>
);

const NavBar = (props: HTMLProps<HTMLDivElement>) => (
  <div className="flex justify-center">
    <div className={twJoin('w-full flex-col px-16', props.className)}>
      <Navbar {...props} className="mb-16 justify-center ls:mb-24 ll:mb-32" />
    </div>
  </div>
);

const Content = ({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className="w-full grow">
    <div
      className={twJoin(
        'mx-auto flex flex-col px-16 pb-64 lm:px-32',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  </div>
);

export const AppPage = { Container, Header, NavBar, Content };
